import styled from '@emotion/styled';
import { Popover, Divider } from 'antd';
import { HTMLAttributes, useMemo } from 'react';
import { useIsSbsMode } from 'hooks/business/useIsSbs';
import { useTranslate } from '@tolgee/react';
import { buyerNickMask } from 'services/channels/demo';
import BuyerDetail from '../BuyerDetail';
import { DistributeIcon } from '../DistributeDetail';
import { useChatHeaderOpen } from 'hooks/business/chat/useChatHeader';
import { ClassicAdjustSalesStatus } from './components/ClassicAdjustSalesStatus';
import { ToolsBar } from './components/ToolsBar';

export interface HeadProps extends HTMLAttributes<HTMLElement> {
  info: Conversation.TopBarInfo;
  isStoreAliasFirst: boolean;
  avatar?: string;
  conversation: Conversation.Conversation;
}

const Head = (props: HeadProps) => {
  const { info, isStoreAliasFirst, avatar, conversation, ...otherProps } = props;

  const isSbsMode = useIsSbsMode();

  const { channel, buyerNick: buyerNickRaw, region, conversationId, storeAlias, storeName } = info;

  const { t } = useTranslate();

  const {
    botMarkEnable,
    adjustSalesStatusEnable,
    toolsBarEnable,
    pauseAutoReplyEnable,
    starEnable,
    blacklistEnable,
    forwardEnable,
  } = useChatHeaderOpen(conversation);

  const buyerNick = useMemo(() => buyerNickMask(buyerNickRaw), [buyerNickRaw]);
  const nickContent = useMemo(() => {
    const nick = <Name>{buyerNick}</Name>;

    if (!channel || !region) {
      return buyerNick;
    }

    return (
      <Popover
        trigger="click"
        placement="bottomLeft"
        content={
          <BuyerDetail platform={channel} region={region} nick={buyerNick} avatar={avatar} />
        }
      >
        {nick}
      </Popover>
    );
  }, [buyerNick, channel, region, avatar]);

  return (
    <Box {...otherProps}>
      <NameWrapper
        style={{
          maxWidth: isSbsMode ? '100%' : '25%',
        }}
      >
        <Nick>{nickContent}</Nick>
        {botMarkEnable ? <RobotTag>{t('sbs.机器人参与接待')}</RobotTag> : null}
      </NameWrapper>
      {adjustSalesStatusEnable && <ClassicAdjustSalesStatus conversation={conversation} />}
      <CDivider type="vertical" />
      <DistributeIcon id={conversationId} />
      <CDivider type="vertical" />
      <ShopNameWrapper>
        <span>{isStoreAliasFirst ? storeAlias || storeName : storeName}</span>
      </ShopNameWrapper>
      {toolsBarEnable && (
        <ToolsBar
          conversation={conversation}
          pauseAutoReplyEnable={pauseAutoReplyEnable}
          starEnable={starEnable}
          blacklistEnable={blacklistEnable}
          forwardEnable={forwardEnable}
        />
      )}
    </Box>
  );
};

const Box = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1px;
  box-shadow: 0 0 1px 0px rgba(0, 0, 0, 0.2);
  background-color: rgb(240, 242, 245);
`;

const Name = styled.div`
  font-size: 14px;
  color: #232426;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Nick = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  flex: 1;
  > img {
    width: 14px;
    height: 14px;
    margin-left: 8px;
  }
`;
const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 25%;
`;
const ShopNameWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  font-size: 14px;
  color: #232426;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin: 0 100px 0 0;
  flex: 1;
  span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;
const CDivider = styled(Divider)`
  margin: 0 10px;
  color: #130000;
`;

const RobotTag = styled.div`
  margin-left: 8px;
  color: #336fff;
  border: 1px solid #d6e8ff;
  border-radius: 2px;
  padding: 0 8px;
  font-size: 12px;
  height: 24px;
  line-height: 24px;
`;

export default Head;
